// The placeholder scene (P0.8): a rotating mesh.
//
// Scaffolding, and honest about it. Its whole job is to be a real Three.js scene rendering real
// frames through a real WebGL2 context, so that the two paths around it — worker and main thread
// — can be proven to work and measured at 60 FPS. The scenes FR-27 actually ships (Reactive Orb,
// Particle Field, Spectrum Tunnel) arrive in M4 against the Architecture §6.2 contract, reading
// FR-26 signals from the ring P0.7 just made live.
//
// Stack §4's seam discipline applies here: this file may use Three freely, and nothing outside
// /visual may import Three at all. `.dependency-cruiser.cjs` enforces the second half.

import {
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PointLight,
  Scene,
  TorusKnotGeometry,
  WebGLRenderer,
} from 'three';

import type { Renderer, RendererInit } from './renderer';

/** Radians per second. Slow enough to look deliberate, fast enough that a stall is obvious. */
const SPIN_X = 0.6;
const SPIN_Y = 0.9;

export class SpinningRenderer implements Renderer {
  readonly #renderer: WebGLRenderer;
  readonly #scene: Scene;
  readonly #camera: PerspectiveCamera;
  readonly #mesh: Mesh;

  constructor(init: RendererInit) {
    this.#renderer = new WebGLRenderer({
      canvas: init.canvas,
      antialias: true,
      // ADR-4: WebGL2 is the v1 baseline. Three falls back to WebGL1 silently otherwise, and a
      // silent fallback to a context we never test is worse than a loud failure.
      powerPreference: 'high-performance',
    });
    this.#renderer.setPixelRatio(init.pixelRatio);
    this.#renderer.setSize(init.width, init.height, false);

    this.#scene = new Scene();

    this.#camera = new PerspectiveCamera(60, init.width / init.height, 0.1, 100);
    this.#camera.position.z = 4;

    this.#mesh = new Mesh(
      new TorusKnotGeometry(1, 0.32, 160, 32),
      new MeshStandardMaterial({ color: 0x4dd0e1, roughness: 0.25, metalness: 0.6 }),
    );
    this.#scene.add(this.#mesh);

    const key = new PointLight(0xffffff, 60);
    key.position.set(3, 4, 5);
    this.#scene.add(key);

    const rim = new PointLight(0xff4081, 30);
    rim.position.set(-4, -2, 2);
    this.#scene.add(rim);
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.#renderer.setPixelRatio(pixelRatio);
    // `false` — do not touch the canvas's CSS size. In the worker path there is no CSS size to
    // touch: the element lives on the main thread and Three cannot see it.
    this.#renderer.setSize(width, height, false);
    this.#camera.aspect = width / height;
    this.#camera.updateProjectionMatrix();
  }

  frame(deltaSeconds: number): void {
    // Rotation scaled by elapsed time, not per frame. A per-frame increment ties the spin's speed
    // to the frame rate, so the same scene runs at half speed on a 30 Hz display and double on a
    // 120 Hz one — and D7 promises "uncapped on high-refresh displays".
    this.#mesh.rotation.x += SPIN_X * deltaSeconds;
    this.#mesh.rotation.y += SPIN_Y * deltaSeconds;

    this.#renderer.render(this.#scene, this.#camera);
  }

  dispose(): void {
    this.#mesh.geometry.dispose();
    (this.#mesh.material as MeshStandardMaterial).dispose();
    this.#renderer.dispose();
  }
}

export function createSpinningRenderer(init: RendererInit): Renderer {
  return new SpinningRenderer(init);
}
